import React from "react";
import { ArrowUpRight, FileText } from "lucide-react";
import { artifact } from "../data/mock";

export default function ArtifactsSection() {
  return (
    <section id="artifacts" className="py-24 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F0F10] text-[#FFE357] text-xs font-semibold uppercase tracking-widest mb-4">
              <FileText className="w-3 h-3" /> Documentation
            </div>
            <h2 className="text-5xl sm:text-6xl font-extrabold tracking-tight">
              Product <span className="squiggle">Artifacts</span>
            </h2>
          </div>
        </div>

        <article className="card-soft p-6 sm:p-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{artifact.title}</h3>
            <h4 className="mt-2 text-2xl text-[#0F0F10]/80 font-semibold">{artifact.subtitle}</h4>
            <p className="mt-6 text-lg text-[#0F0F10]/70 leading-relaxed">{artifact.description}</p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              {artifact.url ? (
                <a
                  href={artifact.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-yellow"
                >
                  Know More
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              ) : (
                <button className="btn-yellow">
                  Know More
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              )}
              <span className="font-serif-italic text-[#0F0F10]/70">{artifact.metric}</span>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-2xl bg-white border-[1.5px] border-[#0F0F10] shadow-[6px_6px_0_#000] overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 bg-[#F5F1EA] border-b border-[#0F0F10]/15">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-green-400"></span>
                <span className="ml-3 font-mono text-xs text-[#0F0F10]/60">PM-Sim — HackerRank</span>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <div className="text-sm font-bold">UX Team Insights</div>
                  <div className="text-xs text-[#0F0F10]/60 mb-2">Recent findings from user research</div>
                  <div className="flex gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                    <span className="text-amber-500">💡</span>
                    <div className="text-xs text-[#0F0F10]/80">
                      <span className="font-semibold">New Recruiter Behavior Patterns</span>
                      <p className="mt-1">Recruiters spend 30% more time on candidate profiles that include video introductions — prioritise the video feature in roadmap.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-bold">Current Scenario</div>
                  <div className="text-xs text-[#0F0F10]/60">Engineers report low morale; CEO demands faster timelines.</div>
                  <div className="mt-2 text-xs font-mono text-red-600">Time Remaining: 3:28</div>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-1">Allocate Resources to Engineering</div>
                  <div className="h-2 rounded-full bg-[#F0E8D8] relative">
                    <div className="absolute left-0 top-0 bottom-0 w-3/4 rounded-full bg-[#0F0F10]"></div>
                    <div className="absolute left-[75%] -top-1 w-4 h-4 rounded-full bg-[#FFE357] border-2 border-[#0F0F10]"></div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold mb-2">Schedule Stakeholder Meeting</div>
                  <div className="flex gap-2">
                    {["This Week", "Next Week", "In Two Weeks"].map((t, i) => (
                      <span
                        key={t}
                        className={`px-2.5 py-1 rounded-md text-xs border ${
                          i === 0 ? "bg-[#0F0F10] text-white border-[#0F0F10]" : "bg-white border-[#0F0F10]/20"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-bold">Product Sense Question</div>
                  <p className="text-xs text-[#0F0F10]/70 mt-1">
                    Given the new UX insights about video introductions, how would you prioritise this in the product roadmap?
                  </p>
                  <div className="mt-2 space-y-1.5 text-xs">
                    {["High Priority — Implement Immediately", "Medium Priority — Plan for Next Quarter", "Low Priority — Long-term Roadmap"].map((opt, i) => (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer">
                        <span
                          className={`w-3.5 h-3.5 rounded-full border-2 ${
                            i === 0 ? "bg-[#0F0F10] border-[#0F0F10]" : "border-[#0F0F10]/40"
                          }`}
                        ></span>
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>

                <button className="w-full mt-2 py-2 rounded-lg bg-[#0F0F10] text-white text-sm font-semibold">
                  Submit Decision
                </button>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 px-3 py-1 rounded-full bg-[#FFE357] border-[1.5px] border-[#0F0F10] text-xs font-bold rotate-[6deg]">
              PRD preview
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
